import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Subscription, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { PatientTaskService } from './../../../../core/services/patient-task.service';
import { PatientService } from './../../../../core/services/patient.service';
import { AssignableMember, taskAssignableMembers, taskAssigneeIds } from './../../../../core/utils/task-assignees';
import { chatTaskCard, knownAssignees, taskPrefill } from '../chat-action-request';
import { ChatService } from './../../../../chat/chat.service';


@Component({
  selector: 'app-patient-task-detail',
  templateUrl: './patient-task-detail.component.html',
  styleUrls: ['./patient-task-detail.component.scss']
})
export class PatientTaskDetailComponent extends BaseComponent implements OnInit {
  todayDate: moment.Moment = moment();
  form!: FormGroup;
  editingTask: any;
  patientId!: string;
  /** The file's patient, for the toolbar heading. */
  patient: any;
  groupId!: string;
  taskStatusOptions: any[] = ['Pending', 'Completed', 'Cancelled'];
  /** Who the "Assigned to" select offers. `tasks/task-assignment.md`. */
  members: AssignableMember[] = [];
  /** The conversation a chat action opened this page from, or null. */
  chatConversationId: string | null = null;
  /** Assignees a chat action sent, held until the members list is here to check them against. */
  private prefillAssignees: string[] = [];

  private routeParamsSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private patientService: PatientService,
    private taskService: PatientTaskService,
    private cd: ChangeDetectorRef,
    private chat: ChatService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();

    this.routeParamsSub = this.route.paramMap.subscribe(params => {
      const noteId = params.get('id');
      if (noteId) {
        this.taskService.getTaskById(noteId)
          .pipe(
            takeUntil(this.destroy$)
          ).subscribe(result => this.useFetchedTask(noteId, result));
      } else {
        this.resetForm();
      }
    });

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[] | null) => {
        if (users == null) {
          // Nothing has loaded the member list yet on this screen.
          this.groupService.getGroupUsers().pipe(takeUntil(this.destroy$)).subscribe();
          return;
        }
        this.members = taskAssignableMembers(users);
        this.assignPrefill();
      });

    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patient = selectedPatient;
            this.patientId = selectedPatient.id;
            this.form.patchValue({ patientId: this.patientId, groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.taskService.editingTask$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(task => {
        this.editingTask = task;
        this.patchForm();
      });


  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
  }

  initForm() {
    this.form = this.fb.group({
      date: [moment(), Validators.required],
      dueDate: [''],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      patientId: [this.patientId, Validators.required],
      task: ['', Validators.required],
      notes: [''],
      status: ['Pending', Validators.required],
      // Empty = the whole practice.
      assigneeUserIds: [[] as string[]],
    });
  }


  patchForm() {
    if (!this.editingTask) {
      return; // Guard clause in case this.editingNote is not yet set
    }

    // Basic patching for directly matched fields
    this.form.patchValue(this.editingTask);
    // The server sends `assignees: [{ userId, name }]`; the select holds ids.
    this.form.patchValue({ assigneeUserIds: taskAssigneeIds(this.editingTask) });

    // For any other special handling or transformations, continue below
    if (this.editingTask.date && !moment.isMoment(this.editingTask.date)) {
      if (this.editingTask.date.length === 10) {
        this.form.patchValue({ date: moment(this.editingTask.date, 'DD/MM/YYYY') });
      } else {
        this.form.patchValue({ date: moment(this.editingTask.date) });
      }
    }

  }


  resetForm() {
    setTimeout(() => {
      this.editingTask = null;
      this.form.reset();
      this.initForm();
      this.applyChatPrefill();
      this.cd.detectChanges();
    });
  }



  /** Opened from a chat action: the description, due day and assignees it sent, and the conversation it came from. */
  private applyChatPrefill(): void {
    const prefill = taskPrefill(this.route.snapshot.queryParamMap);
    this.chatConversationId = prefill.chat;
    if (prefill.text) this.form.patchValue({ task: prefill.text });
    // The datepicker works in UTC days (MAT_MOMENT_DATE_ADAPTER_OPTIONS useUtc).
    if (prefill.due) this.form.patchValue({ dueDate: moment.utc(prefill.due, 'YYYY-MM-DD') });
    this.prefillAssignees = prefill.assignees;
    this.assignPrefill();
  }

  /** The chat's assignees who are in the "Assigned to" list; the rest are dropped. */
  private assignPrefill(): void {
    if (!this.prefillAssignees.length || !this.members.length) return;
    this.form.patchValue({ assigneeUserIds: knownAssignees(this.prefillAssignees, this.members) });
    this.prefillAssignees = [];
  }

  /**
   * Lists set the task they open before navigating here; a chat card opens it by id alone, so the fetched one is
   * edited when nothing else was set for this id.
   */
  private useFetchedTask(id: string, result: any): void {
    const task = result?.data;
    if (task?.id === id && this.editingTask?.id !== id) this.taskService.setEditingTask(task);
  }

  /** A task made from a chat action goes back to that conversation as a card, from the person who saved it. */
  private postChatCard(result: any): void {
    const card = this.chatConversationId ? chatTaskCard(result?.data, this.patient) : null;
    if (!card) return;
    this.chat.store.sendAction(this.chatConversationId!, card).catch((e: unknown) => {
      console.error('Error posting the task to the chat: ', e);
      this.showToastMessage('The task was saved, but could not be posted to the chat.');
    });
  }

  saveTask() {

    const date: moment.Moment = moment.isMoment(this.form.value.date) ? this.form.value.date : moment(this.form.value.date);
    const dueDate: moment.Moment = moment.isMoment(this.form.value.dueDate) ? this.form.value.dueDate : moment(this.form.value.dueDate);


    // Someone assigned earlier who has since left the practice cannot be re-saved (the server refuses
    // anyone who is not an active member), so they drop off here rather than failing the whole save.
    const memberIds = new Set(this.members.map(m => m.userId.toLowerCase()));
    const assigneeUserIds: string[] = (this.form.value.assigneeUserIds || [])
      .filter((id: string) => !this.members.length || memberIds.has(String(id).toLowerCase()));

    const noteData = {
      ...this.form.value,
      assigneeUserIds,
      date: date.utc().startOf('day').toISOString(),
      dueDate: dueDate.utc().startOf('day').toISOString(),
    };

    if (this.editingTask && this.editingTask.id) {
      this.taskService.updateTask(this.editingTask.id, noteData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.showToastMessage('Patient task updated successfully');
            this.returnToParent();
          },
          error: (error) => {
            console.error('Error updating patient task: ', error);
            this.showToastMessage('There was an error updating the patient task. Please try again.');
          }
        });
    } else {
      this.taskService.addTask(noteData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.showToastMessage('Patient task saved successfully');
            this.postChatCard(result);
            this.returnToParent();
          },
          error: (error) => {
            console.error('Error saving patient task: ', error);
            this.showToastMessage('There was an error saving the patient task. Please try again.');
          }
        });
    }
  }


  async deleteTask() {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    this.taskService.deleteTask(this.editingTask.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient task deleted successfully');
          this.returnToParent()
        },
        error: (error) => {
          console.error('Error saving patient task: ', error);
          this.showToastMessage('There was an error deleting the patient task. Please try again.');
        }
      });
  }

  returnToParent() {
    let isDischarge = false;
    const currentUrl = this.router.url;
    if (currentUrl.includes('discharge')) {
      isDischarge = true;
    }
    if (!isDischarge) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patientId}`] } }]);
    } else {
      this.router.navigate(['/patients', { outlets: { patient: ['discharge', `${this.patientId}`] } }]);
    }
  }

}
